import React, { createContext, useState, useEffect, useCallback } from 'react';
import { saveUserData, clearUserData } from "@/utils/authStorage";
import { fetchApi, setAccessToken } from "../api/apiClient";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const login = useCallback((token, userData = null) => {
    setAccessToken(token);
    setIsAuthenticated(true);
    setUser(userData);
    if (userData) {
      saveUserData(userData);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetchApi('/user/logout', 'POST');
    } catch (error) {
      console.error("Logout failed", error);
    }
    setAccessToken(null);
    setIsAuthenticated(false);
    setUser(null);
    clearUserData();
  }, []);

  const initialized = React.useRef(false);

  useEffect(() => {
    const initAuth = async () => {
      // Prevent double call in StrictMode
      if (initialized.current) return;
      initialized.current = true;

      // Check auth status from backend
      const res = await fetchApi('/user/auth/me', 'GET');

      if (res.status && res.data) {
        // Success: User is logged in
        setAccessToken(res.data.jwtToken);
        setIsAuthenticated(true);
        // Assuming response data contains user details directly or nested
        // Adjust based on actual API response structure if needed. 
        // Guide says: Returns a new jwtToken and user details.
        // We might need to map it if structure differs from what login provides.
        // For now, assume res.data keys match what we expect or we construct it.
        const userData = {
          userName: res.data.userName || res.data.user?.userName,
          profileImg: res.data.profileImg || res.data.user?.profileImg,
          profileComplete: res.data.profileComplete || res.data.user?.profileComplete,
          ...res.data.user // spread other fields if available
        };
        setUser(userData);
        saveUserData(userData);
      } else {
        // Not authenticated
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();

    const handleLogoutEvent = () => {
      logout();
    };
    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

